import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Modal, Typography, Upload } from 'antd'
import { Icon } from '@iconify/react'

import {
    STORY_MAP_AGENT_MAX_FILES,
    STORY_MAP_AGENT_MAX_TOTAL_BYTES,
    STORY_MAP_AGENT_TRANSCRIPT_ACCEPT,
    generateAgentStoryMap,
    isAllowedAgentTranscript,
} from '../../../storyMapAgent.service'

const { Text, Title } = Typography

const ACCENT = '#6366f1'

// Modal phases: upload -> processing -> (parent closes on success) / error
const PHASE_UPLOAD = 'upload'
const PHASE_PROCESSING = 'processing'
const PHASE_ERROR = 'error'

const StatusRow = ({ state, label }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 0',
        }}
    >
        {state === 'done' ? (
            <Icon
                icon="mdi:check-circle"
                width={18}
                height={18}
                style={{ color: '#16a34a', flexShrink: 0 }}
            />
        ) : state === 'active' ? (
            <Icon
                icon="mdi:loading"
                width={18}
                height={18}
                style={{ color: ACCENT, flexShrink: 0 }}
                className="storyMapAgentSpin"
            />
        ) : state === 'error' ? (
            <Icon
                icon="mdi:alert-circle"
                width={18}
                height={18}
                style={{ color: '#dc2626', flexShrink: 0 }}
            />
        ) : (
            <Icon
                icon="mdi:circle-outline"
                width={18}
                height={18}
                style={{ color: '#cbd5e1', flexShrink: 0 }}
            />
        )}
        <Text
            style={{
                fontSize: 14,
                color:
                    state === 'pending'
                        ? '#94a3b8'
                        : state === 'error'
                          ? '#dc2626'
                          : 'var(--textColorDarkSlate)',
            }}
        >
            {label}
        </Text>
    </div>
)

/**
 * Storymap Agent popup: pick meeting transcripts, generate. The files are
 * uploaded to S3 via presigned URLs, the AI service turns them into a story
 * map, and `onGenerated(aiResult)` hands the JSON to the parent — which
 * renders it as draft cards on the board. Nothing is persisted here.
 */
const StoryMapAgentModal = ({
    open,
    onCancel,
    storyMapId,
    onPartial,
    onGenerated,
}) => {
    const [phase, setPhase] = useState(PHASE_UPLOAD)
    const [steps, setSteps] = useState([])
    const [activeStepIndex, setActiveStepIndex] = useState(0)
    const [errorMessage, setErrorMessage] = useState('')
    const [fileList, setFileList] = useState([])
    const [fileError, setFileError] = useState('')
    const runIdRef = useRef(0)

    useEffect(() => {
        if (!open) {
            runIdRef.current += 1
            setPhase(PHASE_UPLOAD)
            setFileList([])
            setFileError('')
            setErrorMessage('')
        }
    }, [open])

    const handleFileChange = ({ fileList: nextList }) => {
        setFileError('')
        setFileList(nextList.slice(-STORY_MAP_AGENT_MAX_FILES))
    }

    const validateFiles = (files) => {
        if (files.length === 0) {
            return 'Add at least one transcript file'
        }
        if (files.length > STORY_MAP_AGENT_MAX_FILES) {
            return `A maximum of ${STORY_MAP_AGENT_MAX_FILES} files can be uploaded at once`
        }
        const badFile = files.find((file) => !isAllowedAgentTranscript(file))
        if (badFile) {
            return `"${badFile.name}" is not a supported transcript type (${STORY_MAP_AGENT_TRANSCRIPT_ACCEPT})`
        }
        const totalBytes = files.reduce(
            (sum, file) => sum + (Number(file.size) || 0),
            0
        )
        if (totalBytes > STORY_MAP_AGENT_MAX_TOTAL_BYTES) {
            return 'Combined file size exceeds the 50 MB limit'
        }
        return ''
    }

    const setStepLabel = useCallback((index, label) => {
        setSteps((prev) =>
            prev.map((step, i) => (i === index ? { ...step, label } : step))
        )
    }, [])

    const handleGenerate = useCallback(async () => {
        const files = fileList.map((item) => item.originFileObj || item)
        const validationError = validateFiles(files)
        if (validationError) {
            setFileError(validationError)
            return
        }

        const runId = ++runIdRef.current
        const stillRunning = () => runIdRef.current === runId
        const fail = (message) => {
            if (!stillRunning()) return
            setErrorMessage(message || 'Something went wrong')
            setPhase(PHASE_ERROR)
        }

        setSteps([
            { key: 'upload', label: 'Uploading transcripts (0%)' },
            {
                key: 'generate',
                label: 'Generating stories — this can take a few minutes',
            },
        ])
        setActiveStepIndex(0)
        setPhase(PHASE_PROCESSING)

        // One streaming request does everything: files go up as multipart,
        // NDJSON events come back while the AI works. Upload progress drives
        // the first row; status events relabel the generating row; journey
        // events land on the board as draft cards immediately (via onPartial).
        let journeysStreamed = 0
        const generated = await generateAgentStoryMap(storyMapId, files, {
            onUploadProgress: (fraction) => {
                if (!stillRunning()) return
                setStepLabel(
                    0,
                    `Uploading transcripts (${Math.round(fraction * 100)}%)`
                )
                if (fraction >= 1) setActiveStepIndex(1)
            },
            onEvent: (event) => {
                if (!stillRunning()) return
                if (event?.kind === 'status' && event.message) {
                    setStepLabel(1, event.message)
                } else if (event?.kind === 'journey') {
                    journeysStreamed += 1
                    setStepLabel(
                        1,
                        `${journeysStreamed} ${
                            journeysStreamed === 1 ? 'journey' : 'journeys'
                        } drafted on the board — generating more…`
                    )
                    onPartial?.(event)
                }
            },
        })
        if (!stillRunning()) return
        if (!generated.success) {
            fail(generated.message)
            return
        }

        onGenerated?.(generated.result)
    }, [fileList, onGenerated, onPartial, setStepLabel, storyMapId])

    const processing = phase === PHASE_PROCESSING

    return (
        <Modal
            title={null}
            open={open}
            onCancel={processing ? undefined : onCancel}
            footer={null}
            width={560}
            centered
            maskClosable={false}
            closable={!processing}
            closeIcon={
                <Icon icon="mdi:close" style={{ fontSize: 20, color: '#64748b' }} />
            }
            styles={{
                content: { borderRadius: '12px', padding: 0 },
                body: { padding: '32px' },
            }}
        >
            <style>{`@keyframes storyMapAgentSpin { to { transform: rotate(360deg); } } .storyMapAgentSpin { animation: storyMapAgentSpin 0.9s linear infinite; }`}</style>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Icon
                        icon="mdi:robot-outline"
                        width={22}
                        height={22}
                        style={{ color: ACCENT }}
                    />
                    <Title
                        level={4}
                        style={{ margin: 0, fontSize: 20, fontWeight: 600 }}
                    >
                        Storymap Agent
                    </Title>
                </div>

                {phase === PHASE_UPLOAD ? (
                    <>
                        <Text
                            style={{
                                fontSize: 14,
                                color: '#64748b',
                                lineHeight: '20px',
                            }}
                        >
                            Upload meeting transcripts and the agent will turn
                            them into journeys, steps and stories. New cards
                            appear on the board as a draft you can review before
                            saving.
                        </Text>
                        <Upload.Dragger
                            style={{
                                border: '2px dashed #cbd5e1',
                                borderRadius: 8,
                                backgroundColor: '#f8fafc',
                                padding: '28px 20px',
                            }}
                            fileList={fileList}
                            onChange={handleFileChange}
                            beforeUpload={() => false}
                            multiple
                            accept={STORY_MAP_AGENT_TRANSCRIPT_ACCEPT}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 12,
                                }}
                            >
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 8,
                                        backgroundColor: '#e0e7ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Icon
                                        icon="mdi:file-upload-outline"
                                        width={24}
                                        height={24}
                                        style={{ color: ACCENT }}
                                    />
                                </div>
                                <Text
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: 'var(--textColorDarkSlate)',
                                    }}
                                >
                                    Select or drop transcript files
                                </Text>
                                <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                                    {STORY_MAP_AGENT_TRANSCRIPT_ACCEPT} — up to{' '}
                                    {STORY_MAP_AGENT_MAX_FILES} files, 50 MB total
                                </Text>
                            </div>
                        </Upload.Dragger>
                        {fileError ? (
                            <Text style={{ fontSize: 13, color: '#dc2626' }}>
                                {fileError}
                            </Text>
                        ) : null}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                type="primary"
                                onClick={handleGenerate}
                                disabled={fileList.length === 0}
                                style={{
                                    height: 40,
                                    padding: '0 16px',
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    fontSize: 14,
                                    background: ACCENT,
                                }}
                            >
                                Generate story map
                            </Button>
                        </div>
                    </>
                ) : null}

                {processing ? (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                    >
                        {steps.map((step, index) => (
                            <StatusRow
                                key={step.key}
                                label={step.label}
                                state={
                                    index < activeStepIndex
                                        ? 'done'
                                        : index === activeStepIndex
                                          ? 'active'
                                          : 'pending'
                                }
                            />
                        ))}
                        <Text
                            style={{
                                fontSize: 12,
                                color: '#94a3b8',
                                marginTop: 8,
                            }}
                        >
                            Keep this tab open — the draft will appear on the
                            board when generation finishes.
                        </Text>
                    </div>
                ) : null}

                {phase === PHASE_ERROR ? (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                        }}
                    >
                        <StatusRow
                            state="error"
                            label={errorMessage || 'Something went wrong'}
                        />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 8,
                            }}
                        >
                            <Button onClick={onCancel}>Close</Button>
                            <Button
                                type="primary"
                                style={{ background: ACCENT }}
                                onClick={() => setPhase(PHASE_UPLOAD)}
                            >
                                Try again
                            </Button>
                        </div>
                    </div>
                ) : null}
            </div>
        </Modal>
    )
}

export default StoryMapAgentModal
